<template>
<v-dialog v-model="show" persistent lazy max-width="600">
  <v-card>
    <v-card-title class="title">Подпись документов</v-card-title>
    <v-card-text>
      <v-form ref="sign_form">
        <v-select
          v-if="!is_shared"
          :items="certificates"
          v-model="certificate"
          label="Выберите сертификат"
          item-value="id"
          append-icon="lock"
          :error-messages="certificateErrors"
          return-object
          @input="$v.certificate.$touch()"
          @blur="$v.certificate.$touch()"
        >
          <template slot="selection" slot-scope="data">
              {{ data.item.organization }} - {{ data.item.name }} {{ data.item.title }}
          </template>
          <template slot="item" slot-scope="data">
              {{ data.item.organization }} - {{ data.item.name }} {{ data.item.title }}
          </template>
        </v-select>
        <v-select
          v-if="is_shared"
          v-model="issuer"
          :items="cas"
          :error-messages="issuerErrors"
          label="АЦСК"
          item-text="issuerCN"
          return-object
          @input="$v.issuer.$touch()"
          @blur="$v.issuer.$touch()"
        ></v-select>
        <v-text-field
          v-if="!is_cached"
          ref="pkey"
          v-model="pkey"
          type="file"
          labe="Файл ЭЦП"
          :error-messages="pkeyErrors"
          @input="processFile($event)"
        >
        </v-text-field>
        <v-text-field
          type="password"
          v-model="password"
          label="Пароль к ЭЦП"
          :error-messages="passwordErrors"
          @input="$v.password.$touch()"
          @blur="$v.password.$touch()"
        ></v-text-field>
        <v-checkbox
          v-if="!is_shared && !is_cached"
          label="Сохранить ЭЦП для быстрого доступа"
          color="light-blue darken-3"
          v-model="save_key"
        >
        </v-checkbox>
      </v-form>
      <template v-if="jksCertificates.length > 0">
        Выберите подпись:
        <v-checkbox
          v-for="certificate in jksCertificates"
          :label="certificate.subjTitle"
          color="light-blue darken-3"
          :key="'cert_'+certificate.serial"
          v-model="selectedJKSCertificates"
          :value="certificate"
        >
        </v-checkbox>
      </template>
      <template v-if="loading && !done">
        <b>Статус: </b>{{ status }}
      </template>
      <v-alert v-if="certificate" :value="done" type="success">
        <b>Подписано подписью:</b> {{ certificate.organization }} {{ certificate.name }}
        <template v-if="certificate.title">{{ certificate.title }}</template>
      </v-alert>
      <v-alert v-if="errors.common" :value="true" type="error">
        {{ errors.common[0] }}
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-btn flat @click="reset()">Сбросить</v-btn>
      <v-spacer></v-spacer>
      <v-btn flat @click="closeSignDialog()">Отмена</v-btn>
      <v-btn v-if="!done" color="primary" @click="doSign()" :loading="loading">Подписать</v-btn>
      <v-btn dark v-else color="green" @click="closeSignDialog()">Готово</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
</template>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'
  import { readFileFromComputer, readPrivateKey, signFile, enumJKSPrivateKeys, getJKSPrivateKey, parseCertificate } from '@/utils'
  import { validationMixin } from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'
  import moment from 'moment'

  export default {
    name: 'DocumentSignDialog',
    props: ['value', 'document', 'is_shared'],
    mixins: [validationMixin],
    validations () {
      if (this.is_shared) {
        return {
          issuer: { required },
          pkey: { required },
          password: { required }
        }
      } else {
        if (this.is_cached) 
          return {
            certificate: { required },
            password: { required }
          }
        else
          return {
            certificate: { required },
            pkey: { required },
            password: { required }
          }
      }
    },
    data () {
      return {
        done: false,
        file: null,
        certificate: null,
        jksCertificates: [],
        selectedJKSCertificates: [],
        issuer: null,
        pkey: null,
        loading: false,
        password: '',
        status: '',
        save_key: false,
        errors: {}
      }
    },
    methods: {
      toBase64: function (u8) {
        return btoa(String.fromCharCode.apply(null, u8))
      },
      fromBase64: function(b64encoded) {
        return atob(b64encoded).split("").map(function(c) { return c.charCodeAt(0); })
      },
      async processFile(event) {

        let pkey = this.$refs.pkey.$refs.input.files[0]

        if (!event) {
          this.selectedJKSCertificates = []
          this.jksCertificates = []
          return  
        }

        let readedFile = new Uint8Array(await readFileFromComputer(pkey))

        if (pkey.name.indexOf('.jks') !== -1 && this.is_shared) {

          let aliases = await enumJKSPrivateKeys(readedFile, this.eusign)
          for (let i = 0; i < aliases.length; i++) {
            let endUserInfo = await getJKSPrivateKey(readedFile, aliases[i], this.eusign)
            this.jksCertificates.push(await parseCertificate(endUserInfo.certificates[1], this.eusign))
          }

        }
      },
      reset () {
        this.$refs.sign_form.reset()
        this.$v.$reset()
      },
      closeSignDialog () {
        this.reset()
        this.done = false
        this.loading = false
        this.$emit('close')
      },
      async doSign() {

        delete this.errors.common
        this.$v.$touch()

        if (this.$v.$invalid)
          return

        this.loading = true

        this.status = 'Считывание ключа'

        let readedFile = null
        let issuer = null
        let keys = []

        if (this.is_shared) {
          issuer = this.issuer
        } else {
          issuer = this.cas[this.certificate.issuer_index]
        }

        let isJKS = false

        if (this.is_cached) {
          let pkey_encoded = JSON.parse(localStorage.getItem('key_'+this.certificate.serial)).pkey
          readedFile = new Uint8Array(this.fromBase64(pkey_encoded))
          keys.push(readedFile)
        } else {
          let pkey = this.$refs.pkey.$refs.input.files[0]
          readedFile = new Uint8Array(await readFileFromComputer(pkey))

          if (pkey.name.indexOf('.jks') !== -1) {
            isJKS = true
            let aliases = await enumJKSPrivateKeys(readedFile, this.eusign)
            let found = false
            for (let i = 0; i < aliases.length; i++) {
              let endUserInfo = await getJKSPrivateKey(readedFile, aliases[i], this.eusign)
              let cert = await readPrivateKey(endUserInfo.privateKey, this.password, issuer, this.eusign)

              if (this.is_shared) {
                for (let j = 0; j < this.selectedJKSCertificates.length; j++) {
                  if (this.selectedJKSCertificates[j].serial === cert.serial) {
                    keys.push(endUserInfo.privateKey)
                  }
                }
              } else {
                if (cert.serial === this.certificate.serial) {
                  keys.push(endUserInfo.privateKey)
                  found = true
                }
              }

            }

            if (!found && !this.is_shared) {
              this.loading = false
              this.errors.common = ['Выбранный сертификат не найден в JKS файле.']
              return
            }
          } else {
            keys.push(readedFile)
          }
        }

        if (isJKS
            && this.selectedJKSCertificates.length === 0
            && this.is_shared) {
          this.loading = false
          this.errors.common = ['Не выбран сертификат для подписи.']
          return
        }

        for (let i = 0; i < keys.length; i++) {
          let cert = await readPrivateKey(keys[i], this.password, issuer, this.eusign)

          if (cert.errorCode) {
            this.loading = false
            this.errors.common = [cert.message]
            return
          }

          if (this.save_key) {
            localStorage.setItem('key_'+cert.serial, JSON.stringify({
              pkey: this.toBase64(keys[i]),
              dtime: moment().format('DD-MM-Y')
            }))
          }

          let document = this.document

          this.status = `${document.name} - Скачиваниe`
          let token = localStorage.getItem('token')

          let url = `/v1/documents/${document.id}/original/get?access_token=${token}`
          if (this.is_shared) {
            url = `/v1/documents/${document.id}/original/get`
          }

          let response = await Vue.axios.get(url,
            {responseType: 'blob'})

          this.status = `${document.name} - Наложение внешней подписи`

          let external_sign = await signFile(response.data, 1, true, true, this.eusign)

          let fd = new FormData()
          fd.append('sign', new Blob([external_sign.sign]))
          fd.append('is_external', true)

          await this.$store.dispatch('postSign', {document_id: document.id, fd})
          
        }

        this.done = true
        this.$emit('done')
      }
    },
    computed: {
      ...mapState(['certificates', 'cas', 'eusign']),
      show: {
        get () {
          return this.value
        },
        set (newValue) {
          if (!newValue)
            this.$emit('close')
        }
      },
      is_cached () {
        if (!this.certificate)
          return false

        return localStorage.getItem('key_'+this.certificate.serial)
      },
      certificateErrors () {
        let errors = []
        if (!this.$v.certificate.$dirty || this.is_shared) return errors
        !this.$v.certificate.required && errors.push('Не выбран сертификат.')
        return errors
      },
      issuerErrors () {
        let errors = []
        if (!this.$v.issuer.$dirty || !this.is_shared) return errors
        !this.$v.issuer.required && errors.push('Обязательное поле.')
        return errors
      },
      pkeyErrors () {
        let errors = []
        if (!this.$v.pkey.$dirty) return errors
        !this.$v.pkey.required && errors.push('Не выбран файл с ЭЦП.')
        return errors
      },
      passwordErrors () {
        let errors = []
        if (!this.$v.password.$dirty) return errors
        !this.$v.password.required && errors.push('Обязательное поле.')
        return errors
      }
    }
  }
</script>